/**
 * AddFieldPanel — AK-334/335 «Tap-to-field» bottom-sheet + тосты.
 *
 * Монтируется в v2.tsx под styleReady когда режим «+ Поле» активен. Рисует
 * нижнюю панель для фаз машины addFieldStore:
 *   - detecting → чип «Ищу границы…» (пульс на точке рисует контроллер)
 *   - draft     → «≈ N га · Примерные границы» + [Сохранить][Редактировать][Заново]
 *   - form      → форма: Название (автоген) + Хозяйство (+ «Новое хозяйство»
 *                 инлайн) + Культура (опц.) + [Сохранить поле]
 * Плюс top-center тосты: ошибка детекции / успех сохранения.
 *
 * Только @/components/ui/* + Lucide + Earth Tech токены (ноль hex вне whitelist).
 * Панель НЕ держит геометрию — читает черновик из addFieldStore, сохранение
 * делает через api.post; MVT-инвалидацию дергает через onSaved (v2.tsx).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Crosshair, Loader2, Pencil, RotateCcw, X, Plus, Check, FolderUp, Satellite } from 'lucide-react';
import { ImportDropzone } from './ImportDropzone';
import { RecentImportsList } from './RecentImportsList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { useAddFieldStore } from '@/lib/map/addField/addFieldStore';
import { preselectFarmId } from '@/lib/map/addField/preselectFarm';
import { nextFieldName } from '@/lib/map/addField/autoName';
import { cropLabel, type CropLike } from '@/lib/cropLabel';
import { useInvalidateSetupStatus } from '@/lib/onboarding/useSetupStatus';

interface Farm {
  id: string;
  company_id: string;
  cluster_id: string | null;
  name: string;
}

// Reference /crops отдаёт name_translations (JSON {ru,en}) + latin_name/code —
// НЕ name_ru. Локализованное имя читаем через общий cropLabel (AK-336).
type CropRef = CropLike;

interface CropsResponse {
  total: number;
  items: CropRef[];
}

interface FieldNameRow {
  name: string;
}

interface FieldListResponse {
  items?: FieldNameRow[];
}

/** Форматирует площадь в «≈ N га» (1 знак ≥10 га, иначе 2). */
function formatAreaHa(ha: number | null): string {
  if (ha == null || !Number.isFinite(ha) || ha <= 0) return '—';
  const digits = ha >= 10 ? 1 : 2;
  return `${ha.toFixed(digits).replace('.', ',')} га`;
}

/**
 * Кнопка-сегмент способа добавления (AK-373). role=tab, активная — solid
 * (bg-background + тень внутри muted-трека), как iOS segmented control.
 */
function MethodTab({
  active,
  icon,
  label,
  testid,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  testid: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testid}
      onClick={onClick}
      className={[
        'flex flex-col items-center justify-center gap-1 rounded-md px-1 py-2 text-xs font-medium transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {icon}
      {label}
    </button>
  );
}

interface AddFieldPanelProps {
  /** Вызывается после успешного сохранения поля — v2.tsx делает MVT-инвалидацию. */
  onSaved: () => void;
}

export function AddFieldPanel({ onSaved }: AddFieldPanelProps) {
  // AK-584: первое сохранённое поле снимает признак «настройка не закончена» —
  // сбрасываем сводку, чтобы янтарный баннер ушёл сам, без перезагрузки.
  const invalidateSetup = useInvalidateSetupStatus();
  const phase = useAddFieldStore((s) => s.phase);
  const method = useAddFieldStore((s) => s.method);
  const setMethod = useAddFieldStore((s) => s.setMethod);
  const draftGeometry = useAddFieldStore((s) => s.draftGeometry);
  const draftAreaHa = useAddFieldStore((s) => s.draftAreaHa);
  const sessionFarmId = useAddFieldStore((s) => s.sessionFarmId);
  const errorToast = useAddFieldStore((s) => s.errorToast);
  const successToast = useAddFieldStore((s) => s.successToast);
  const reset = useAddFieldStore((s) => s.reset);
  const requestEdit = useAddFieldStore((s) => s.requestEdit);
  const requestManualDraw = useAddFieldStore((s) => s.requestManualDraw);
  const openForm = useAddFieldStore((s) => s.openForm);
  const completeSave = useAddFieldStore((s) => s.completeSave);
  const deactivate = useAddFieldStore((s) => s.deactivate);
  const clearErrorToast = useAddFieldStore((s) => s.clearErrorToast);
  const clearSuccessToast = useAddFieldStore((s) => s.clearSuccessToast);

  // Справочники (грузим лениво один раз при монтировании панели).
  const [farms, setFarms] = useState<Farm[]>([]);
  const [crops, setCrops] = useState<CropRef[]>([]);
  const [existingNames, setExistingNames] = useState<string[]>([]);

  // Форма.
  const [fieldName, setFieldName] = useState('');
  const [farmId, setFarmId] = useState<string>('');
  const [cropId, setCropId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // AK-584: когда хозяйство известно заранее (вход «добавить поле в ЭТО
  // хозяйство» / запомнено с прошлого сохранения / оно единственное), форма
  // показывает его строкой-фактом со ссылкой «сменить», а не пустым
  // выпадающим списком. Спрашивать «в какое хозяйство?» человека, который
  // только что стоял внутри хозяйства, — лишний вопрос.
  const [farmPickerOpen, setFarmPickerOpen] = useState(false);

  // Инлайн-создание хозяйства.
  const [newFarmOpen, setNewFarmOpen] = useState(false);
  const [newFarmName, setNewFarmName] = useState('');
  const [creatingFarm, setCreatingFarm] = useState(false);

  // AK-373: способ «Файл» — дропзона показывается когда method==='import'
  // (сегмент-переключатель или вход с `?addField=1&import=1` через
  // activateWithImport, который сам ставит method='import'). Отдельного
  // локального importOpen больше нет — единый источник правды = store.method.

  // Загрузка справочников + имён полей для автогенерации.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [fr, cr, names] = await Promise.all([
        api.get<Farm[]>('/api/v1/farms').catch(() => [] as Farm[]),
        api
          .get<CropsResponse>('/api/v1/reference/crops?limit=500')
          .catch(() => ({ total: 0, items: [] }) as CropsResponse),
        api
          .get<FieldListResponse>('/api/v1/fields?limit=1000')
          .catch(() => ({ items: [] }) as FieldListResponse),
      ]);
      if (!alive) return;
      setFarms(fr);
      setCrops(cr.items ?? []);
      setExistingNames((names.items ?? []).map((r) => r.name).filter(Boolean));
    })();
    return () => {
      alive = false;
    };
  }, []);

  // При входе в форму — автоген имени и подстановка запомненного хозяйства.
  const autoName = useMemo(() => nextFieldName(existingNames), [existingNames]);

  // Дубль имени виден СРАЗУ при вводе, а не после «Сохранить»: ошибка, которая
  // приходит после сохранения, заставляет переделывать уже сделанное. Правило
  // (решение founder 14.08, Р3): у одного клиента имена уникальны, у разных
  // клиентов «Северное-1» жить может. Сервер тем же правилом отвечает 409 —
  // здесь только быстрый ответ по уже загруженному списку имён.
  const duplicateName = useMemo(() => {
    const typed = fieldName.trim().toLowerCase();
    if (!typed) return false;
    return existingNames.some((n) => n.trim().toLowerCase() === typed);
  }, [fieldName, existingNames]);
  useEffect(() => {
    if (phase !== 'form') return;
    setFieldName((prev) => prev || autoName);
    // Правило выбора — в `preselectFarmId` (там же и объяснение, почему
    // единственное хозяйство подставляется само).
    setFarmId((prev) => preselectFarmId(prev, sessionFarmId, farms));
    setFarmPickerOpen(false);
    setFormError(null);
  }, [phase, autoName, sessionFarmId, farms]);

  // Имя предвыбранного хозяйства для строки-факта. Пусто (в т.ч. пока не
  // подгрузился список ферм) → показываем обычный выпадающий список.
  const preselectedFarmName = useMemo(() => {
    if (!farmId || farmPickerOpen || newFarmOpen) return null;
    return farms.find((f) => f.id === farmId)?.name ?? null;
  }, [farmId, farmPickerOpen, newFarmOpen, farms]);

  // Автодизмисс тостов (успех 4с, ошибка 5с).
  useEffect(() => {
    if (!successToast) return;
    const t = window.setTimeout(clearSuccessToast, 4000);
    return () => window.clearTimeout(t);
  }, [successToast, clearSuccessToast]);
  useEffect(() => {
    if (!errorToast) return;
    const t = window.setTimeout(clearErrorToast, 5000);
    return () => window.clearTimeout(t);
  }, [errorToast, clearErrorToast]);

  const handleCreateFarm = async (): Promise<void> => {
    const name = newFarmName.trim();
    if (!name) return;
    // company_id берём от любого существующего хозяйства (карта — один тенант,
    // компания у всех ферм одна и та же в типовом кейсе). Если ферм ещё нет —
    // тянем companies.
    let companyId: string | null = farms[0]?.company_id ?? null;
    if (!companyId) {
      try {
        const companies = await api.get<{ id: string }[]>('/api/v1/companies');
        companyId = companies[0]?.id ?? null;
      } catch {
        companyId = null;
      }
    }
    if (!companyId) {
      setFormError('Не удалось определить компанию для нового хозяйства.');
      return;
    }
    setCreatingFarm(true);
    try {
      const created = await api.post<Farm>('/api/v1/farms', {
        company_id: companyId,
        name,
      });
      setFarms((prev) => [...prev, created]);
      setFarmId(created.id);
      setNewFarmOpen(false);
      setNewFarmName('');
    } catch (e) {
      setFormError(e instanceof ApiError ? e.detail : 'Не удалось создать хозяйство.');
    } finally {
      setCreatingFarm(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!draftGeometry) return;
    const name = fieldName.trim();
    if (!name) {
      setFormError('Введите название поля.');
      return;
    }
    if (duplicateName) {
      setFormError(`Поле «${name}» здесь уже есть — выберите другое название.`);
      return;
    }
    if (!farmId) {
      setFormError('Выберите хозяйство.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload: Record<string, unknown> = {
        farm_id: farmId,
        name,
        geometry: draftGeometry,
      };
      if (cropId) payload.culture_id = cropId;
      await api.post<{ id: string }>('/api/v1/fields', payload);
      const farmName = farms.find((f) => f.id === farmId)?.name ?? 'хозяйство';
      // Форму под следующий цикл: имя сбрасываем (переавтогенерится),
      // хозяйство запоминается в сторе.
      setExistingNames((prev) => [...prev, name]);
      setFieldName('');
      setCropId('');
      completeSave(`«${name}» добавлено в «${farmName}»`, farmId, farmName);
      invalidateSetup();
      onSaved();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.detail : 'Не удалось сохранить поле.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* ── Top-center тосты ─────────────────────────────────────────── */}
      {errorToast && (
        <div
          data-testid="add-field-error-toast"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-40 max-w-md rounded-full bg-foreground text-background text-xs font-medium px-4 py-2 shadow-lg flex items-center gap-2"
        >
          <span>{errorToast}</span>
          <button
            type="button"
            aria-label="Закрыть"
            onClick={clearErrorToast}
            className="opacity-70 hover:opacity-100"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {successToast && (
        <div
          data-testid="add-field-success-toast"
          className="absolute top-4 left-1/2 -translate-x-1/2 z-40 max-w-md rounded-full bg-primary text-primary-foreground text-xs font-semibold px-4 py-2 shadow-lg"
        >
          {successToast}
        </div>
      )}

      {/* ── AK-373: панель способа добавления (сегмент-переключатель) ──────
          Opaque-карточка (bg-popover), НЕ плавающие полупрозрачные чипы —
          иначе текст тонет на спутнике (п.1+3). Три способа видны ЯВНО
          (п.4а: автопоиск больше не скрыт в жест-тап). */}
      {phase === 'armed' && (
        <div
          data-testid="add-field-method-panel"
          className="absolute top-14 left-1/2 -translate-x-1/2 z-30 w-[min(320px,calc(100%-32px))] rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl p-3"
        >
          {/* Сегмент-переключатель 3 способов. */}
          <div
            role="tablist"
            aria-label="Способ добавления поля"
            className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1 mb-3"
          >
            <MethodTab
              active={method === 'detect'}
              icon={<Crosshair size={14} />}
              label="Автопоиск"
              testid="add-field-method-detect"
              onClick={() => setMethod('detect')}
            />
            <MethodTab
              active={method === 'draw'}
              icon={<Pencil size={14} />}
              label="Нарисовать"
              testid="add-field-method-draw"
              onClick={() => setMethod('draw')}
            />
            <MethodTab
              active={method === 'import'}
              icon={<FolderUp size={14} />}
              label="Файл"
              testid="add-field-method-import"
              onClick={() => setMethod('import')}
            />
          </div>

          {/* Контент выбранного способа. */}
          {method === 'detect' && (
            <div className="flex flex-col gap-2">
              <div data-testid="add-field-hint" className="flex items-start gap-2 text-xs">
                <Crosshair size={14} className="mt-1 shrink-0 text-accent-brand" />
                <span className="text-foreground">
                  Тапните на поле на карте — найдём границы автоматически.
                </span>
              </div>
              {/* AK-377 Track1 «перцептивный фикс детекта»: пока идёт автопоиск,
                  карта показывает ту же Sentinel-подложку (10 м), по которой
                  реально ищет детектор. Честный бейдж управляет ожиданием:
                  контур примерный, точность = разрешение Sentinel, а не ESRI. */}
              <div
                data-testid="add-field-sentinel-badge"
                className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs leading-snug text-muted-foreground"
              >
                <Satellite size={14} className="mt-1 shrink-0" />
                <span>
                  На карте — спутниковая основа детектора (Sentinel-2, 10 м).
                  Контур примерный.
                </span>
              </div>
            </div>
          )}

          {method === 'draw' && (
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2 text-xs">
                <Pencil size={14} className="mt-1 shrink-0 text-accent-brand" />
                <span className="text-foreground">
                  Поставьте точки по углам поля кликами, замкните контур на первой
                  точке.
                </span>
              </div>
              <Button
                variant="default"
                size="sm"
                className="w-full"
                data-testid="add-field-manual-draw"
                onClick={() => requestManualDraw()}
              >
                <Pencil size={14} />
                Начать рисование
              </Button>
            </div>
          )}

          {method === 'import' && (
            <div data-testid="add-field-import-card">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground mb-2">
                <FolderUp size={14} className="text-accent-brand" />
                Импорт полей из файла
              </div>
              <ImportDropzone />
              {/* AK-369: «Последние импорты» с откатом/восстановлением. Сам
                  прячется если пусто / бэк не готов (graceful). onChanged →
                  MVT-инвалидация (тот же onSaved, что и при добавлении поля). */}
              <RecentImportsList onChanged={onSaved} />
            </div>
          )}
        </div>
      )}

      {/* ── Detecting чип ────────────────────────────────────────────── */}
      {phase === 'detecting' && (
        <div
          data-testid="add-field-detecting"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 rounded-full bg-popover border border-border text-foreground text-xs font-semibold px-4 py-2 shadow-lg flex items-center gap-2"
        >
          <Loader2 size={14} className="animate-spin text-accent-brand" />
          Ищу границы…
        </div>
      )}

      {/* ── Bottom-sheet: draft / form ───────────────────────────────── */}
      {(phase === 'draft' || phase === 'form') && (
        <div
          data-testid="add-field-sheet"
          // Непрозрачная поверхность (bg-popover), НЕ полупрозрачность над картой —
          // иначе спутник просвечивает и текст «Сохранить поле» тонет (AK-373 п.1+3,
          // паттерн opaque-дропзоны #413).
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(420px,calc(100%-32px))] rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl p-4"
        >
          {/* Заголовок + закрыть */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Plus size={16} className="text-accent-brand" />
              {phase === 'draft' ? 'Новое поле' : `Сохранить поле · ${formatAreaHa(draftAreaHa)}`}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 -mt-1 -mr-1"
              aria-label="Выйти из режима"
              data-testid="add-field-close"
              onClick={() => deactivate()}
            >
              <X size={16} />
            </Button>
          </div>

          {phase === 'draft' && (
            <>
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2 mb-3">
                <div className="text-xl font-bold tabular-nums text-foreground leading-tight">
                  ≈ {formatAreaHa(draftAreaHa)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Примерные границы · можно уточнить
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  data-testid="add-field-save-draft"
                  onClick={() => openForm()}
                >
                  Сохранить
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid="add-field-edit"
                  onClick={() => requestEdit()}
                >
                  <Pencil size={14} />
                  Редактировать
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="add-field-again"
                  onClick={() => reset()}
                >
                  <RotateCcw size={14} />
                  Заново
                </Button>
              </div>
            </>
          )}

          {phase === 'form' && (
            <div className="flex flex-col gap-3">
              <div className="space-y-2">
                <Label htmlFor="af-name">Название</Label>
                <Input
                  id="af-name"
                  data-testid="add-field-name"
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  placeholder={autoName}
                  maxLength={500}
                />
                {duplicateName && (
                  <p className="text-xs text-destructive" data-testid="add-field-name-duplicate">
                    Такое название здесь уже есть.{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => setFieldName(autoName)}
                    >
                      Назвать «{autoName}»
                    </button>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="af-farm">Хозяйство</Label>
                {preselectedFarmName ? (
                  <div
                    data-testid="add-field-farm-fact"
                    className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2"
                  >
                    <span className="truncate text-sm font-medium text-foreground">
                      {preselectedFarmName}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto shrink-0 p-0 text-xs text-muted-foreground"
                      data-testid="add-field-farm-change"
                      onClick={() => setFarmPickerOpen(true)}
                    >
                      сменить
                    </Button>
                  </div>
                ) : !newFarmOpen ? (
                  <>
                    <Select
                      value={farmId || undefined}
                      onValueChange={(v) => {
                        if (v === '__new__') {
                          setNewFarmOpen(true);
                          return;
                        }
                        setFarmId(v);
                      }}
                    >
                      <SelectTrigger id="af-farm" data-testid="add-field-farm">
                        <SelectValue placeholder="Выберите хозяйство" />
                      </SelectTrigger>
                      <SelectContent>
                        {farms.map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__new__" data-testid="add-field-new-farm-option">
                          {/* Иконка вместо эмодзи «➕»: тот же вес и цвет, что у
                              остальных глифов панели (зона 1 · А9). */}
                          <span className="flex items-center gap-2">
                            <Plus size={14} aria-hidden />
                            Новое хозяйство
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      autoFocus
                      data-testid="add-field-new-farm-name"
                      value={newFarmName}
                      onChange={(e) => setNewFarmName(e.target.value)}
                      placeholder="Название хозяйства"
                      maxLength={500}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleCreateFarm();
                        if (e.key === 'Escape') setNewFarmOpen(false);
                      }}
                    />
                    <Button
                      size="sm"
                      data-testid="add-field-new-farm-create"
                      disabled={!newFarmName.trim() || creatingFarm}
                      onClick={() => void handleCreateFarm()}
                    >
                      {creatingFarm ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      Создать
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      aria-label="Отменить создание хозяйства"
                      onClick={() => {
                        setNewFarmOpen(false);
                        setNewFarmName('');
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="af-crop">Культура · необязательно</Label>
                <Select value={cropId || undefined} onValueChange={setCropId}>
                  <SelectTrigger id="af-crop" data-testid="add-field-crop">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {crops.map((c) => (
                      <SelectItem key={String(c.id)} value={String(c.id)}>
                        {cropLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formError && (
                <div className="text-xs text-destructive" data-testid="add-field-form-error">
                  {formError}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  disabled={saving || duplicateName}
                  data-testid="add-field-save"
                  onClick={() => void handleSave()}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                  Сохранить поле
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="add-field-back-to-draft"
                  onClick={() => reset()}
                >
                  Отмена
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Drawing хинт (способ «Нарисовать», поточечное рисование) ──────
          Пока юзер ставит точки в редакторе, держим opaque-подсказку сверху
          + «Отмена» (secondary) для чистого выхода из режима (AK-373 п.4б/6). */}
      {phase === 'drawing' && (
        <div
          data-testid="add-field-drawing-hint"
          className="absolute top-14 left-1/2 -translate-x-1/2 z-30 w-[min(320px,calc(100%-32px))] rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl p-3 flex flex-col gap-2"
        >
          <div className="flex items-start gap-2 text-xs">
            <Pencil size={14} className="mt-1 shrink-0 text-accent-brand" />
            <span className="text-foreground">
              Ставьте точки по углам поля кликами. Замкните контур, кликнув по
              первой точке — затем можно двигать вершины.
            </span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            data-testid="add-field-drawing-cancel"
            onClick={() => deactivate()}
          >
            <X size={14} />
            Отмена
          </Button>
        </div>
      )}
    </>
  );
}
